import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import Spinner from '../../../../../common/spinner/Spinner.jsx';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import {
  useDeleteInterestMutation,
  useLazyDownloadCvQuery,
  useLazyDownloadMotivationLetterQuery,
} from '../../../../../../redux/services/fj_interests.jsx';
import { useDataGridDateRangeFilter } from '../../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import CollapseBlock from '../../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { handleDownload } from '../../../../../../utils/helpers/handleDownload.js';

const FjInterestsTable = ({
  interestedData,
  refetchInterestedData,
  canDeleteInterest,
}) => {
  const { t } = useTranslation();

  const [deleteInterest] = useDeleteInterestMutation();
  const [triggerDownloadCv] = useLazyDownloadCvQuery();
  const [triggerDownloadMotivationLetter] =
    useLazyDownloadMotivationLetterQuery();
  const { deleteSuccess } = useToastMessages();
  const handleDeleteInterest = (id) => {
    deleteInterest(id)
      .unwrap()
      .then(() => {
        deleteSuccess();
        refetchInterestedData();
      });
  };

  // Table Definition
  const studentLastNameTextFilterProps = useDataGridTextFilter('lastName');
  const studentFirstNameTextFilterProps = useDataGridTextFilter('firstName');
  const mailTextFilterProps = useDataGridTextFilter('mail');
  const phoneTextFilterProps = useDataGridTextFilter('phone');
  const createdAtFilterProps = useDataGridDateRangeFilter('created_at');

  const dataGridColumns = useMemo(
    () => [
      {
        title: t('Priezvisko'),
        dataIndex: 'lastName',
        width: 150,
        defaultSortOrder: 'ascend',
        sorter: dataGridTextSorter('lastName'),
        ...studentLastNameTextFilterProps,
      },
      {
        title: t('Meno'),
        dataIndex: 'firstName',
        width: 150,
        sorter: dataGridTextSorter('firstName'),
        ...studentFirstNameTextFilterProps,
      },
      {
        title: t('Email'),
        dataIndex: 'mail',
        width: 275,
        sorter: dataGridTextSorter('mail'),
        ...mailTextFilterProps,
      },
      {
        title: t('Telefón'),
        dataIndex: 'phone',
        sorter: dataGridTextSorter('phone'),
        ...phoneTextFilterProps,
      },
      {
        title: t('Dátum prihlásenia'),
        dataIndex: 'created_at',
        sorter: dataGridDateSorter('created_at'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...createdAtFilterProps,
      },
      {
        title: t('Životopis'),
        dataIndex: 'path_resume',
        link: true,
        render: (path_resume, row) => {
          const displayName = path_resume
            ? path_resume.split('-').slice(1).join('-')
            : '-';
          if (!path_resume) return <span>-</span>;
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(
                  'cv',
                  row,
                  displayName,
                  triggerDownloadCv,
                  triggerDownloadMotivationLetter,
                );
              }}
            >
              {displayName}
            </a>
          );
        },
      },
      {
        title: t('Motivačný list'),
        dataIndex: 'path_motiv_letter',
        link: true,
        render: (path_motiv_letter, row) => {
          const displayName = path_motiv_letter
            ? path_motiv_letter.split('-').slice(1).join('-')
            : '-';
          if (!path_motiv_letter) return <span>-</span>;
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(
                  'motiv',
                  row,
                  displayName,
                  triggerDownloadCv,
                  triggerDownloadMotivationLetter,
                );
              }}
            >
              {displayName}
            </a>
          );
        },
      },
    ],
    [
      t,
      studentFirstNameTextFilterProps,
      studentLastNameTextFilterProps,
      mailTextFilterProps,
      phoneTextFilterProps,
      createdAtFilterProps,
      triggerDownloadCv,
      triggerDownloadMotivationLetter,
    ],
  );

  return !interestedData ? (
    <Spinner />
  ) : (
    <>
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <DataGrid
            rowKey={(interest) => interest.id}
            columns={dataGridColumns}
            dataSource={interestedData || []}
            permissionsBasic={'fri-jobs-interest'}
            uniqueTableId={'fri-jobs-interest-table'}
            optionsWidth={225}
            onDelete={(record) => {
              handleDeleteInterest(record.id);
            }}
            disableSettings={!canDeleteInterest}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

FjInterestsTable.propTypes = {
  interestedData: PropTypes.array,
  refetchInterestedData: PropTypes.func,
  canDeleteInterest: PropTypes.bool,
};

export default FjInterestsTable;
